import { ContextKindKey } from '@gonfalon/contexts/types';
import { rejectEmptySegmentMatchClauses } from '@gonfalon/dogfood-flags/dogfood-flags';
import type { List } from '@gonfalon/immutable-shim/immutableShim';
import { Map, Record } from '@gonfalon/immutable-shim/immutableShim';
import { testSemVer } from '@gonfalon/legacy-form-validation/validationUtils';
import { msg } from '@gonfalon/messages/msg';
import { isEmpty } from 'lodash';

import type { Clause } from '#utils/clause/records/Clause';
import { OS_NAME_ATTRIBUTE, OS_VERSION_ATTRIBUTE } from '#utils/constants';

import { invalidRegExp, invalidSemVer, oneOf, required } from './errors';
import { invalidAttribute } from './targeting';

const semVerOps = ['semVerEqual', 'semVerLessThan', 'semVerGreaterThan'];
const ops = [
  'in',
  'endsWith',
  'startsWith',
  'matches',
  'contains',
  'lessThan',
  'lessThanOrEqual',
  'greaterThan',
  'greaterThanOrEqual',
  'before',
  'after',
  'segmentMatch',
  'applicationVersionSupported',
];

export class ClauseValidation extends Record({
  attribute: Map(),
  op: Map(),
  values: Map(),
}) {
  isEmpty() {
    return this.attribute.isEmpty() && this.op.isEmpty() && this.values.isEmpty();
  }

  isInvalid() {
    return (
      !this.isEmpty() &&
      (this.getIn(['op', 'type']) === 'oneOf' ||
        this.getIn(['values', 'type']) === 'invalidRegExp' ||
        this.getIn(['values', 'type']) === 'invalidSemVer' ||
        this.getIn(['attribute', 'type']) === 'invalidAttribute')
    );
  }

  isIncomplete() {
    return (
      !this.isEmpty() &&
      (this.getIn(['attribute', 'type']) === 'required' ||
        this.getIn(['op', 'type']) === 'required' ||
        this.getIn(['values', 'type']) === 'required')
    );
  }

  getMessage() {
    if (this.isEmpty()) {
      return;
    }

    if (this.isInvalid()) {
      if (this.getIn(['op', 'type']) === 'oneOf') {
        return msg('targeting.clauseValidation.operatorRequired');
      }

      if (this.getIn(['values', 'type']) === 'invalidRegExp') {
        return msg('targeting.clauseValidation.invalidRegex');
      }

      if (this.getIn(['values', 'type']) === 'invalidSemVer') {
        return msg('targeting.clauseValidation.invalidSemver');
      }

      if (this.getIn(['attribute', 'type']) === 'invalidAttribute') {
        const attributeName = this.getIn(['attribute', 'name']);
        if (attributeName === OS_VERSION_ATTRIBUTE) {
          return msg('targeting.clauseValidation.osNameRequired');
        }
        return msg('targeting.clauseValidation.invalidAttribute', { attributeName });
      }
    }

    if (this.isIncomplete()) {
      if (this.getIn(['attribute', 'type']) === 'required') {
        return msg('targeting.clauseValidation.attributeRequired');
      }

      if (this.getIn(['values', 'type']) === 'required') {
        return msg('targeting.clauseValidation.segmentRequired');
      }
    }
  }
}

export function validateClause(clause: Clause, allClauses?: List<Clause>) {
  let validation = new ClauseValidation();
  const validOps = [...ops, ...semVerOps];
  // remove this once OS version matching is released
  const showOSVersionWarning = false;

  if (isEmpty(clause.attribute) && clause.op !== 'segmentMatch') {
    validation = validation.set('attribute', required(clause.attribute));
  }

  if (clause.attribute === 'secondary') {
    validation = validation.set('attribute', invalidAttribute(clause.attribute));
  }
  if (
    showOSVersionWarning &&
    allClauses &&
    clause.contextKind === ContextKindKey.DEVICE &&
    clause.attribute === OS_VERSION_ATTRIBUTE
  ) {
    const hasOSNameClause = allClauses?.filter(({ attribute }) => attribute && attribute === OS_NAME_ATTRIBUTE);
    if (hasOSNameClause?.size === 0) {
      validation = validation.set('attribute', invalidAttribute(clause.attribute));
    }
  }

  if (!clause.op) {
    validation = validation.set('op', required(clause.op));
  } else if (!validOps.includes(clause.op)) {
    validation = validation.set('op', oneOf(validOps, clause.op));
  } else if (clause.op === 'segmentMatch' && clause.values.isEmpty() && rejectEmptySegmentMatchClauses()) {
    validation = validation.set('values', required(clause.values));
  } else if (clause.op === 'matches') {
    try {
      RegExp(clause.values.first('').toString());
    } catch {
      validation = validation.set('values', invalidRegExp(clause.values));
    }
  } else if (clause.op.startsWith('semVer')) {
    if (!clause.values.every((value) => testSemVer(value.toString()))) {
      validation = validation.set('values', invalidSemVer(clause.values));
    }
  }

  return validation;
}
